import { z } from "zod";

export const BoolSchema = z.preprocess((val) => {
  if (val === true) return true;
  if (val === undefined || val === 'undefined') return undefined;
  if (val === null || val === 'null') return null;
  if (typeof val === 'string') {
    const s = val.trim();
    if (s === '') return undefined;
    if (s === '1'
      || s.toLowerCase() === 'true'
      || s.toLowerCase() === 'yes'
      || s.toLowerCase() === 'on'
    ) return true;
    if (s === '0'
      || s.toLowerCase() === 'false'
      || s.toLowerCase() === 'no'
      || s.toLowerCase() === 'off'
    ) return false;
    // 其它字符串尝试数字化
    const num = Number(s);
    if (!Number.isNaN(num)) return num !== 0;
    return undefined; // 无法解析的字符串视为未提供
  }
  if (typeof val === 'number') return val !== 0;
  if (typeof val === 'boolean') return val;
  return false;
}, z.boolean()) as z.ZodType<boolean>;
export type BoolType = z.infer<typeof BoolSchema>;

export const MobileSchema = z
  .string()
  .regex(/^1[3-9]\d{9}$/, "请输入正确的手机号码");
export type MobileType = z.infer<typeof MobileSchema>;

// 邮箱验证
export const EmailSchema = z
  .string()
  .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "请输入有效的邮箱地址");
export type EmailType = z.infer<typeof EmailSchema>;



export const UnixTimestampSchema = z.preprocess((val) => {
  if (val === "0" || val === 0 || val == null) return null; // 返回 null 以便 nullable 处理
  if (val instanceof Date) return val;
  if (typeof val === "number") {
    return new Date(val * 1000);
  }
  if (typeof val === "string") {
    if (/^\d+$/.test(val)) {
      const num = Number(val);
      return new Date(num * 1000);
    }
    const d = new Date(val); // 尝试解析 ISO 字符串
    if (!isNaN(d.getTime())) return d;
  }
  // 如果都失败了，返回 null 表示无效时间戳
  console.warn("Invalid timestamp value:", val);
  return null;
}, z.date().nullable()) as z.ZodType<Date | null>;
export type UnixTimestampType = z.infer<typeof UnixTimestampSchema>;

export const CaptChaSchema = z.object({
  code: z.string().min(1, "请输入验证码."),
  key: z.string().min(1, "验证码标识丢失,请刷新页面重试."),
});

export type CaptchaType = z.infer<typeof CaptChaSchema>;

export const CaptChaParam = {
  captcha: CaptChaSchema.optional(),
};

// 分页字段定义（用于 FilterParamSchema.extend() 和 PageParam）
export const PageDataParam = {
  page: z.coerce.number().min(1, "页码最小为1").optional(),
  limit: z.coerce
    .number()
    .min(1, "返回结果最小为1")
    .max(100, "返回值最大为100")
    .optional(),
} as const;

export const PageParam = {
  page: z.object(PageDataParam),
  count_num: BoolSchema.default(false),
};

export type PageType = z.infer<typeof PageParam.page>;

// 偏移分页字段定义（用于 FilterParamSchema.extend() 和 LimitParam）
export const LimitDataParam = {
  pos: z.coerce.number().min(0, "起始位置最小为0").nullable().optional(), //可选。第一页传null。
  limit: z.coerce
    .number()
    .min(1, "返回结果最小为1")
    .max(100, "返回值最大为100")
    .optional(),
  forward: BoolSchema.optional(), //向前翻页(下一页)为true，向后翻页(上一页)为false，默认为true
  more: BoolSchema.optional(), //尝试检查是否还有下一页数据
} as const;

export const LimitParam = {
  limit: z.object(LimitDataParam),
  count_num: BoolSchema.default(false),
};

export type LimitType = z.infer<typeof LimitParam.limit>;

export const PageResSchema = {
  total: z.coerce.number().nullable().optional(),
};

// 其余可解析的数字/数字字符串 -> number
export const NumberParamSchema = z.preprocess((val) => {
  // 空字符串应视为未提供 -> undefined（用于移除 URL 参数）
  if (val === '' || (typeof val === 'string' && val.trim() === '')) return undefined;
  if (val === 'null' || val === null) return null;
  if (val === 'undefined' || val === undefined) return undefined;
  if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val.getTime();
  if (typeof val === 'number') return Number.isNaN(val) ? null : val;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    const num = Number(trimmed);
    return Number.isNaN(num) ? null : num;
  }
  // 其它类型视为无效 -> null
  return null;
}, z.number().nullable().optional()) as z.ZodType<number | null | undefined>;
export type NumberParamType = z.infer<typeof NumberParamSchema>;

// 服务端 TotalRecrodResp 结构: { exact: number|null, over: number|null }
export const TotalRecrodResSchema = z.object({
  exact: NumberParamSchema,
  over: NumberParamSchema,
});
export type TotalRecrodResType = z.infer<typeof TotalRecrodResSchema>;

// 游标分页响应结构，服务端嵌套在 cursor 字段内
export const CursorPageResSchema = z.object({
  next: z.coerce.number().nullable().optional(),
  prev: z.coerce.number().nullable().optional(),
});

export type CursorPageResType = z.infer<typeof CursorPageResSchema>;

export const LimitResSchema = {
  cursor: CursorPageResSchema.nullable().optional(),
  total: TotalRecrodResSchema.nullable().optional(),
};

// 用户数据结构
export const UserDataResSchema = z.object({
  /** 用户所属应用ID */
  app_id: z.coerce.number(),
  /** 用户ID */
  id: z.coerce.number(),
  /** 用户账号 */
  user_account: z.string(),
  /** 用户数据 */
  user_data: z.string(),
  /** 用户昵称 */
  user_nickname: z.string(),
});

export type UserDataResType = z.infer<typeof UserDataResSchema>;




